import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import { Form, Modal, Select, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import { getInstitutions } from "../../services/institutionService";
import TextArea from "../../components/Inputs/TextAreaInput";

import {
  createTechnologyInitiative,
  deleteTechnologyInitiative,
  getTechnologyInitiatives,
  updateTechnologyInitiative,
} from "../../services/technologyInitiativeService";
import { technologyInitiativeSchema } from "../../validators/technologyInitiativeValidators";
import { getFaculties } from "../../services/facultyService";
import { getSessions } from "../../services/sessionService";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const TechnologyInitiative = () => {
  const [initiatives, setInitiatives] = useState([]);
  const [filteredInitiatives, setFilteredInitiatives] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initiativeToEdit, setInitiativeToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [initiativeToDelete, setInitiativeToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");
  const [sessions, setSessions] = useState([]);
  const [selectedFaculty, setSelectedFaculty] = useState("Not Selected");

  const { user } = useStateContext();

  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadInitiatives();
    loadFaculties();
    loadInstitutions();
    loadSessions();
  }, []);

  useEffect(() => {
    let filtered = initiatives;

    if (searchTerm.trim() !== "") {
      filtered = initiatives.filter((item) =>
        item?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    if (selectedFaculty !== "Not Selected") {
      filtered = filtered.filter(
        (item) => item.faculty?.id?.toString() === selectedFaculty?.toString()
      );
    }

    setFilteredInitiatives(filtered);
  }, [searchTerm, initiatives, selectedInstitution, selectedFaculty]);

  const loadInstitutions = async () => {
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      toast.error("Failed to load institutions");
    }
  };

  const loadSessions = async () => {
    try {
      const { data } = await getSessions();
      setSessions(data.data);
    } catch (error) {
      toast.error("Failed to load sessions");
    }
  };

  const loadFaculties = async () => {
    try {
      const { data } = await getFaculties();
      setFaculties(data.data);
    } catch (error) {
      toast.error("Failed to load faculties");
    }
  };

  const loadInitiatives = async () => {
    setLoading(true);
    try {
      const { data } = await getTechnologyInitiatives();

      setInitiatives(data.data);
      setFilteredInitiatives(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load Projects");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (initiativeToEdit) {
      onUpdateInitiative(payload, resetForm);
    } else {
      onCreateInitiative(payload, resetForm);
    }
  };

  const onCreateInitiative = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createTechnologyInitiative(payload);

      setInitiatives((prev) => [...prev, data.data]);

      toast.success("Project added successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(response.data.error || "Project was not added, try again!");
      }
    }
  };

  const handleEdit = (category) => {
    setInitiativeToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdateInitiative = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalInitiatives = [...initiatives];

      const initiativeIndex = originalInitiatives.findIndex(
        (initiative) => initiative.id === initiativeToEdit.id
      );

      if (initiativeIndex === -1) throw new Error("Project not found.");

      const { data } = await updateTechnologyInitiative(
        initiativeToEdit.id,
        payload
      );

      originalInitiatives[initiativeIndex] = data.data;

      setInitiatives(originalInitiatives);

      setInitiativeToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Project updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error ||
            "Projects & Innovation not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setInitiativeToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteInitiative = async () => {
    const originalInitiatives = [...initiatives];

    const newInitiatives = originalInitiatives.filter(
      (initiative) => initiative.id !== initiativeToDelete.id
    );

    setInitiatives(newInitiatives);

    try {
      await deleteTechnologyInitiative(initiativeToDelete?.id);

      toast.success("Project deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Project not deleted, try again later"
        }`
      );

      setInitiatives(originalInitiatives);
    } finally {
      setIsDeleteModalOpen(false);

      setInitiativeToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setInitiativeToEdit(null);
    setInitiativeToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: ["institution", "name"],
            key: "institution",
            render: (text) => (
              <span className="text-gray-700 text-sm">{text || "N/A"}</span>
            ),
          },
        ]
      : []),
    {
      title: "Faculty",
      dataIndex: "faculty",
      key: "faculty",
      render: (_, record) => {
        const text = record?.faculty?.name;
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "center", color: "#888" }}>
            {displayText}
          </div>
        ) : (
          <>{displayText}</>
        );
      },
    },
    {
      title: "Year",
      dataIndex: ["session", "name"],
      key: "session",
      render: (text) => (
        <span className="text-gray-700 text-sm">{text || "N/A"}</span>
      ),
    },
    {
      title: "Project",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "left", color: "#888" }}>{displayText}</div>
        ) : (
          <Tooltip title={text}>
            <div
              style={{
                maxWidth: 250,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {text}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Project">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Project">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Projects & Innovations / STEAM"
        subtitle1="Projects & Innovations"
      />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Projects & Innovations / STEAM"
            count={filteredInitiatives.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Project"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredInitiatives.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              <div className="mb-2 py-2 flex gap-2">
                {!isInstAdmin && (
                  <div className="mb-2 py-2 flex gap-2">
                    <Select
                      multiple={false}
                      id="institutions"
                      defaultValue={selectedInstitution}
                      className="w-full h-[36px] min-w-[250px]"
                      onChange={(value) => {
                        setSelectedInstitution(value);
                      }}
                      showSearch={true}
                      filterOption={(input, option) =>
                        option?.children
                          ?.toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      <Select.Option value={"Not Selected"}>
                        Filter by Institution
                      </Select.Option>
                      {institutions &&
                        institutions.length > 0 &&
                        institutions.map((option) => (
                          <Select.Option key={option.id} value={option.id}>
                            {option.name}
                          </Select.Option>
                        ))}
                    </Select>
                  </div>
                )}
                <Select
                  multiple={false}
                  id="faculties"
                  defaultValue={selectedFaculty}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => {
                    setSelectedFaculty(value);
                  }}
                  showSearch={true}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  <Select.Option value={"Not Selected"}>
                    Filter by Faculty
                  </Select.Option>
                  {faculties &&
                    faculties.length > 0 &&
                    faculties.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                </Select>
              </div>
            }
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {initiativeToEdit ? "Edit Project" : "Add Project"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                name: initiativeToEdit ? initiativeToEdit?.name : "",
                description: initiativeToEdit
                  ? initiativeToEdit?.description
                  : "",
                faculty_id: initiativeToEdit
                  ? initiativeToEdit?.faculty?.id
                  : "",

                institution_id: initiativeToEdit
                  ? initiativeToEdit?.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
                session_id: initiativeToEdit
                  ? initiativeToEdit?.session?.id
                  : "",
              }}
              enableReinitialize
              validationSchema={technologyInitiativeSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                submitForm,
              }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    <TextInput
                      name="name"
                      title="Project Name"
                      placeholder="e.g. MakeTech: Youth Innovation Bootcamp"
                      requiredField
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <TextArea
                      name="description"
                      title="Description"
                      placeholder="e.g. A hands-on technology program aimed at equipping youth with skills in robotics, coding, and green tech innovations."
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    {isInstAdmin ? (
                      <input
                        type="hidden"
                        name="institution_id"
                        value={
                          initiativeToEdit
                            ? initiativeToEdit?.institution?.id
                            : user?.institution?.id
                        }
                      />
                    ) : (
                      <CustomDropDownInput
                        name="institution_id"
                        title="Institution"
                        placeholder="Select an institution"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={institutions}
                        setFieldValue={setFieldValue}
                      />
                    )}

                    <CustomDropDownInput
                      name="faculty_id"
                      title="Faculty"
                      placeholder="Select a faculty"
                      errors={errors}
                      values={values}
                      touched={touched}
                      displayLabel="name"
                      idLabel="id"
                      setFieldValue={setFieldValue}
                      options={faculties.filter(
                        (faculty) =>
                          faculty.institution?.id === values.institution_id
                      )}
                    />

                    <CustomDropDownInput
                      name="session_id"
                      title="Year"
                      placeholder="Select a year"
                      requiredField
                      errors={errors}
                      values={values}
                      touched={touched}
                      displayLabel="name"
                      idLabel="id"
                      options={sessions}
                      setFieldValue={setFieldValue}
                    />

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteInitiative}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default TechnologyInitiative;
